import {BehaviorSubject, Observable} from 'rxjs';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {FilterState} from '@theme/components';
import {GenericPopoutFilter} from '@core/interfaces/common/popout';
import {Filter, FilterFieldType, FilterOperatorType, FilterType} from '@core/interfaces/system/system-common';
import {debounceTime, filter, share, shareReplay, startWith, takeUntil, tap} from 'rxjs/operators';
import {OutageFilterGroup} from '../../../../pages/geospatial-viewer/api/geospatial-viewer-controls.service';
import {FilterAccordion, FiltersFullState} from '@theme/components/layout/control-panel/filter-accordion';

// TODO: this was hastily separated out from generic-filter-accordion.ts and filter-accordion as the parent for both.
// This has significant overlap and should consolidate common methods.
export abstract class GenericFilterGroupAccordion extends FilterAccordion {
    get currentFilters(): Filter[] {
        const _formGroupValue = this.formGroupValue.value;
        if (_formGroupValue) return this.mapFormGroupValue(_formGroupValue);
        return [];
    }

    public filtersFormGroupChanged$ = new BehaviorSubject<boolean>(false);

    protected formGroup = this.fb.group({});

    protected filterGroupSettings: BehaviorSubject<OutageFilterGroup[]> = new BehaviorSubject<OutageFilterGroup[]>(
        null,
    );
    readonly filterGroupSettings$: Observable<OutageFilterGroup[]> = this.filterGroupSettings.asObservable().pipe(
        filter((settings) => settings != null),
        share(),
        tap((settings) => {
            this.changeSubscription && this.changeSubscription.unsubscribe();
            this.searchSubscription && this.searchSubscription.unsubscribe();
            this.initOutageLayerFilters(settings);
        }),
        shareReplay(1),
    );

    public refresh = new BehaviorSubject<boolean>(false);
    readonly refresh$: Observable<boolean> = this.refresh.asObservable();

    private savedFiltersFullState = new BehaviorSubject<FiltersFullState>({});

    public getFilterState(fieldKey: string): FilterState {
        return this.savedFiltersFullState.value[fieldKey];
    }

    constructor(public fb: FormBuilder) {
        super(fb);
    }

    get filtersFormGroupControls(): any {
        return this.filtersFormGroup?.controls;
    }

    get filtersFormGroup(): FormGroup {
        return this.formGroup.get('filters') as FormGroup;
    }

    get searchStringFormControl(): FormControl {
        return this.formGroup.get('searchString') as FormControl;
    }

    private subscribeOnChanges() {
        this.changeSubscription = this.filtersFormGroup.valueChanges
            .pipe(
                takeUntil(this.unsubscribe$),
                startWith(this.filtersFormGroup.value),
                tap((value) => {
                    if (this.formGroupValue.value === null) {
                        this.formGroupValue.next(JSON.parse(JSON.stringify(value)));
                    }
                }),
                filter((value) => {
                    return !this.isEquivalent(this.formGroupValue.value, value);
                }),
                debounceTime(500),
            )
            .subscribe((value) => {
                this.formGroupValue.next(JSON.parse(JSON.stringify(value)));
            });
    }

    // TODO: add support for every filter type.
    protected mapFormGroupValue(formGroupValue: any): Filter[] {
        // Filters
        const filterList: Filter[] = [];

        this.filterGroupSettings.value.forEach((group: OutageFilterGroup) => {
            group.filters.forEach((f: GenericPopoutFilter) => {
                let options: string[] = [];

                if (FilterType.isSelectFilterType(f.webFilterType)) {
                    formGroupValue[f.fieldKey]?.forEach((option) => {
                        options.push(option.key);
                    });

                    if (f.webFilterType == FilterType.MultiSelectAutocomplete) {
                        if (options.length > 0 && f.options.length !== options.length)
                            filterList.push({
                                fieldKey: f.fieldKey,
                                fieldType: f.fieldType,
                                operator: f.operator,
                                values: options,
                            });
                    } else if (f.webFilterType == FilterType.MultiSelectAutocompleteApi) {
                        // Do not add filters if there are no selected values
                        if (options.length > 0) {
                            let _newItem: Filter = {
                                fieldKey: f.fieldKey,
                                fieldType: f.fieldType,
                                operator: f.operator,
                            };

                            if (this.getFilterState(f.fieldKey)?.isAllSelected) {
                                _newItem.value = this.getFilterState(f.fieldKey)?.searchString;
                                _newItem.operator = FilterOperatorType.CONTAIN;
                            } else _newItem.values = options;

                            filterList.push(_newItem);
                        }
                    }
                } else if (f.webFilterType === FilterType.DateRange || f.webFilterType === FilterType.DatetimeRange) {
                    let _value = formGroupValue[f.fieldKey],
                        min = _value?.start,
                        max = _value?.end;

                    if (min || max) {
                        options = [min ?? f.min, max ?? f.max];

                        filterList.push({
                            fieldKey: f.fieldKey,
                            fieldType: f.fieldType,
                            operator: f.operator,
                            values: options,
                        });
                    }
                } else if (f.webFilterType === FilterType.NumericRange) {
                    let _value = formGroupValue[f.fieldKey],
                        min = _value?.min,
                        max = _value?.max;

                    if (min || max) {
                        options = [min ?? f.min, max ?? f.max];

                        filterList.push({
                            fieldKey: f.fieldKey,
                            fieldType: f.fieldType,
                            operator: f.operator,
                            values: options,
                        });
                    }
                }
            });
        });

        return filterList;
    }

    public destroy() {
        this.changeSubscription && this.changeSubscription.unsubscribe();
        this.searchSubscription && this.searchSubscription.unsubscribe();
        this.filtersFormGroup?.reset();
        this.searchStringFormControl?.reset();
    }

    /** Assume all filters are unique, and consolidate all groups into just a single formGroup of filters */
    private initOutageLayerFilters(groups: OutageFilterGroup[]): void {
        this.initFilters(groups);
        this.resetFilterGroupsFullState(groups);
        this.subscribeOnChanges();
    }

    private initFilters(filterGroups: OutageFilterGroup[]): void {
        const filters = this.fb.group({});

        filterGroups.forEach((group: OutageFilterGroup) => {
            group?.filters.forEach((f: GenericPopoutFilter) => {
                if (FilterType.isMultiValueFilter(f.webFilterType)) {
                    filters.setControl(f.fieldKey, this.fb.control([]));
                } else if (FilterType.isSingleValueFilter(f.webFilterType)) {
                    filters.setControl(f.fieldKey, this.fb.control({}));
                }
            });
        });

        this.setFiltersFormGroup(filters);
    }

    public resetGeospatialFilters() {
        this.filtersFormGroup?.reset();
        this.searchStringFormControl?.reset();
    }

    private setFiltersFormGroup(filters: FormGroup) {
        if (this.filtersFormGroup) {
            this.filtersFormGroup.reset(filters.value);
            Object.keys(filters.value).forEach((prop) => {
                this.filtersFormGroupControls[prop]?.enable(); // null check because it may be checking filters from other layers/formgroups
            });
        } else {
            this.formGroup.setControl('filters', filters);
            this.filtersFormGroupChanged$.next(true);
        }
    }

    public isEquivalent(val1: any, val2: any): boolean {
        if (val1 instanceof Object && val2 instanceof Object) {
            const val1props = Object.keys(val1);
            const val2props = Object.keys(val2);

            if (val1props.length !== val2props.length) return false;

            for (let i = 0; i < val1props.length; i++) {
                if (val1props[i] === 'enabled') continue;
                const val2Prop = val2props.find((item) => item === val1props[i]);
                if (!val2Prop) return false;

                if (!this.isEquivalent(val1[val2Prop], val2[val2Prop])) return false;
            }
            return true;
        }
        return val1 === val2;
    }

    public resetFilterGroupsFullState(initialFilterGroups: OutageFilterGroup[]) {
        const newFiltersState: FiltersFullState = {};

        // Consolidate filters from all groups into single list
        initialFilterGroups.forEach((group: OutageFilterGroup) => {
            group.filters.forEach((f: GenericPopoutFilter) => {
                if (FilterType.isSelectFilterType(f.webFilterType)) {
                    newFiltersState[f.fieldKey] = {
                        isAllSelected: false,
                        searchString: '',
                        selectedOptions: [],
                    };
                }
            });
        });

        this.savedFiltersFullState.next(newFiltersState);
    }

    public updateFiltersFullState(fieldKey: string, state: FilterState) {
        const newFiltersState: FiltersFullState = this.savedFiltersFullState.value;
        newFiltersState[fieldKey] = state;
        this.savedFiltersFullState.next(newFiltersState);
    }
}
